// Regras de permissão por position (cargo).
// Fonte única de verdade compartilhada entre o router e a navegação (LayoutNavigation).

// master tem acesso irrestrito a todas as áreas.
const MASTER_POSITION = "master";

// Retorna true se a position estiver na lista de permitidas.
export const hasAnyPosition = (position, allowedPositions) =>
  !!position && allowedPositions.includes(position);

// Retorna true se a position tiver valor e não estiver na lista de bloqueadas.
export const isNotPosition = (position, deniedPositions) =>
  !!position && !deniedPositions.includes(position);

// Garante que master sempre passe, independente da regra específica.
const allowMaster = (rule) => (position) =>
  position === MASTER_POSITION || rule(position);

/** Leads — modules/leads */
export function resolveMarketingLeadsEnabled (modules = {}) {
  return modules.leads === true
}

/** Landing page — modules/landingPage */
export function resolveLandingPageEnabled (modules = {}) {
  return modules.landingPage === true
}

/** Business Plan — modules/businessPlan */
export function resolveMarketingBusinessPlanEnabled (modules = {}) {
  return modules.businessPlan === true
}

/** Modais internos — modules/campaigns */
export function resolveMarketingCampaignsEnabled (modules = {}) {
  return modules.campaigns === true
}

/** Afiliados → Anúncios — modules/affiliatesMarketing */
export function resolveAffiliatesMarketingEnabled (modules = {}) {
  return modules.affiliatesMarketing === true
}

/** MoneyHub → Dashboard — modules/affiliatesDashboard */
export function resolveAffiliatesDashboardEnabled (modules = {}) {
  return modules.affiliatesDashboard === true
}

/** MoneyHub → Afiliados — modules/affiliatesFinance */
export function resolveAffiliatesFinanceEnabled (modules = {}) {
  return modules.affiliatesFinance === true
}

/** MoneyHub → Adicionar indicações — modules/affiliatesAddIndication */
export function resolveAffiliatesAddIndicationEnabled (modules = {}) {
  return modules.affiliatesAddIndication === true
}

/** MoneyHub → Planos — modules/affiliatesPlans */
export function resolveAffiliatesPlansEnabled (modules = {}) {
  return modules.affiliatesPlans === true
}

/** AcademyHub — modules/academyHub */
export function resolveAcademyHubEnabled (modules = {}) {
  return modules.academyHub === true
}

/** Pesquisa NPS — modules/npsSurveys */
export function resolveNpsSurveysEnabled (modules = {}) {
  return modules.npsSurveys === true
}

/** Aplicativos — modules/aplicativos */
export function resolveAplicativosEnabled (modules = {}) {
  return modules.aplicativos === true
}

/** Abas do modal de app — "Informações gerais" é sempre visível para quem acessa Aplicativos */
export const APP_DETAIL_TAB_MODULES = Object.freeze({
  progress: 'aplicativosProgress',
  requests: 'aplicativosRequests',
  branding: 'aplicativosBranding'
})

export function resolveAppDetailTabAccess (position, modules = {}) {
  return Object.keys(APP_DETAIL_TAB_MODULES).reduce((access, tab) => {
    access[tab] = position === MASTER_POSITION || modules[APP_DETAIL_TAB_MODULES[tab]] === true
    return access
  }, {})
}

/** Servidores → Processos — modules/serversProcessos */
export function resolveServersProcessosEnabled (modules = {}) {
  return modules.serversProcessos === true
}

/** Servidores → Fila de XML — modules/serversXml */
export function resolveServersXmlEnabled (modules = {}) {
  return modules.serversXml === true
}

/** Servidores → Custos Firebase — modules/serversFirebaseCosts */
export function resolveServersFirebaseCostsEnabled (modules = {}) {
  return modules.serversFirebaseCosts === true
}

/** Servidores → Servidor Connect — modules/serversConnect */
export function resolveServersConnectEnabled (modules = {}) {
  return modules.serversConnect === true
}

/** Clientes → Dashboard — modules/clientsDashboard */
export function resolveClientsDashboardEnabled (modules = {}) {
  return modules.clientsDashboard === true
}

/** Clientes → Score — modules/clientsScore */
export function resolveClientsScoreEnabled (modules = {}) {
  return modules.clientsScore === true
}

/** Clientes → Upsell — modules/clientsUpsell */
export function resolveClientsUpsellEnabled (modules = {}) {
  return modules.clientsUpsell === true
}

/** Cancelamentos — modules/cancelamentos */
export function resolveCancelamentosEnabled (modules = {}) {
  return modules.cancelamentos === true
}

/** Cupons — modules/coupons */
export function resolveCouponsEnabled (modules = {}) {
  return modules.coupons === true
}

/** Comercial — modules/commercial */
export function resolveCommercialEnabled (modules = {}) {
  return modules.commercial === true
}

/** Autônomos → Upsells — modules/autonomousUpsells */
export function resolveAutonomousUpsellsEnabled (modules = {}) {
  return modules.autonomousUpsells === true
}

/** Autônomos → Self-service — modules/autonomousSelfService */
export function resolveAutonomousSelfServiceEnabled (modules = {}) {
  return modules.autonomousSelfService === true
}

/** Menu Autônomos — master ou Upsells / Self-service habilitados */
export function canAccessAutonomousRoute (position, modules = {}) {
  if (position === MASTER_POSITION) return true
  return (
    resolveAutonomousUpsellsEnabled(modules) ||
    resolveAutonomousSelfServiceEnabled(modules)
  )
}

/** Financeiro → Contratos — modules/financeContratos */
export function resolveFinanceContratosEnabled (modules = {}) {
  return modules.financeContratos === true
}

/** Financeiro → Excedentes — modules/financeExcedentes */
export function resolveFinanceExcedentesEnabled (modules = {}) {
  return modules.financeExcedentes === true
}

/** Rota /finance/ — master ou Contratos / Excedentes habilitados */
export function canAccessFinanceRoute (position, modules = {}) {
  if (position === MASTER_POSITION) return true
  return (
    resolveFinanceContratosEnabled(modules) ||
    resolveFinanceExcedentesEnabled(modules)
  )
}

/** Alterar contratos e transações — master ou Contratos habilitado */
export function canEditFinanceContracts (affiliate = {}) {
  if (!affiliate) return false
  if (affiliate.position === MASTER_POSITION) return true
  return affiliate.modules?.financeContratos === true
}

/** Excluir clientes marcados — master ou modules/clientsRemoveMarked */
export function canRemoveMarkedCustomers (affiliate = {}) {
  if (!affiliate) return false
  if (String(affiliate.position || "").toLowerCase() === MASTER_POSITION) return true
  return affiliate.modules?.clientsRemoveMarked === true
}

/** Rota /servers/ — master ou Processos / Fila de XML / Custos Firebase / Servidor Connect habilitados */
export function canAccessServersRoute (position, modules = {}) {
  if (position === MASTER_POSITION) return true
  return (
    resolveServersProcessosEnabled(modules) ||
    resolveServersXmlEnabled(modules) ||
    resolveServersFirebaseCostsEnabled(modules) ||
    resolveServersConnectEnabled(modules)
  )
}

const CLIENTS_FULL_ACCESS_POSITIONS = ["adm", "administrador", "support", "operador", "marketing"];

// Predicados de acesso por área. Cada um recebe a position e retorna boolean.
export const permissions = {
  seeAllClients: allowMaster((position) => {
    if (!position) return false;
    return CLIENTS_FULL_ACCESS_POSITIONS.includes(String(position).toLowerCase());
  }),
  reservas: allowMaster((position) => isNotPosition(position, ["outbound"])),
  cancelamentos: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
  coupons: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
  commercial: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
  autonomousUpsells: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
  autonomousSelfService: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
  finance: (position, modules = {}) => canAccessFinanceRoute(position, modules),
  financeContratos: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
  financeExcedentes: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
  updates: allowMaster((position) => hasAnyPosition(position, ["adm", "master"])), // criar, editar e gerenciar
  npsSurveys: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  aplicativos: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  academyHub: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  serversProcessos: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  serversXml: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  serversFirebaseCosts: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  serversConnect: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  master: (position) => position === MASTER_POSITION,
  /** MoneyHub → Dashboard — modules/affiliatesDashboard */
  affiliatesDashboard: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  /** MoneyHub → Afiliados — modules/affiliatesFinance */
  affiliatesFinance: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  /** MoneyHub → Adicionar indicações — modules/affiliatesAddIndication */
  affiliatesAddIndication: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  /** MoneyHub → Planos — modules/affiliatesPlans */
  affiliatesPlans: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
  /** Afiliados → Anúncios — modules/affiliatesMarketing */
  affiliatesMarketing: (position, moduleEnabled) => {
    if (position === MASTER_POSITION) return true;
    return moduleEnabled === true;
  },
    marketingLeads: (position, leadsEnabled) =>
    position === MASTER_POSITION || leadsEnabled === true,
  marketingLandingPage: (position, landingPageEnabled) =>
    position === MASTER_POSITION || landingPageEnabled === true,
  marketingBusinessPlan: (position, businessPlanEnabled) =>
    position === MASTER_POSITION || businessPlanEnabled === true,
  marketingCampaigns: (position, campaignsEnabled) =>
    position === MASTER_POSITION || campaignsEnabled === true,
  carteiras: (position, walletModuleEnabled) =>
    position === MASTER_POSITION || walletModuleEnabled === true,
  /** Ver todas as carteiras/clientes na tela Carteiras */
  carteirasFullAccess: (position, walletModuleEnabled) =>
    walletModuleEnabled === true || permissions.seeAllClients(position),
  /** Migração em massa e ações administrativas na tela Carteiras */
  carteirasManage: (position, walletModuleEnabled) => {
    if (walletModuleEnabled === true || position === MASTER_POSITION) return true
    const role = String(position || '').toLowerCase()
    return role === 'adm' || role === 'administrador'
  },
  /** Dashboard em Suporte — modules/supportDashboard */
  suporteDashboard: (position, supportDashboardEnabled) =>
    position === MASTER_POSITION || supportDashboardEnabled === true,
  /** Chamados (bug report) — modules/bugReport */
  chamados: (position, bugReportEnabled) =>
    position === MASTER_POSITION || bugReportEnabled === true,
  /** Clientes → Dashboard — modules/clientsDashboard */
  clientsDashboard: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
  /** Clientes → Score — modules/clientsScore */
  clientsScore: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
  /** Clientes → Upsell — modules/clientsUpsell */
  clientsUpsell: (position, moduleEnabled) =>
    position === MASTER_POSITION || moduleEnabled === true,
};

